import { useState } from "preact/hooks";
import type { UiPresetStyles } from "../config/presets";
import { TextField } from "./TextField";
import { Toggle } from "./Toggle";

export type CallRequestFormData = {
	firstName: string;
	lastName: string;
	phone: string;
	email: string;
	message: string;
	consent: boolean;
};

type CallRequestFormProps = {
	formData: CallRequestFormData;
	styles: UiPresetStyles;
	headingText?: string | null;
	isLoading: boolean;
	error: string | null;
	onSubmit: (e: Event) => void;
	onFieldChange: (
		field: keyof CallRequestFormData,
		value: string | boolean,
	) => void;
};

export function CallRequestForm({
	formData,
	styles,
	headingText,
	isLoading,
	error,
	onSubmit,
	onFieldChange,
}: CallRequestFormProps) {
	const [messageParamEnabled] = useState(() =>
		new URLSearchParams(window.location.search).has("message"),
	);
	const [preferMessage, setPreferMessage] = useState(false);
	const displayHeading = headingText?.trim() ?? "";

	return (
		<>
			{displayHeading && (
				<h1 className={styles.headingClassName}>{displayHeading}</h1>
			)}

			<form onSubmit={onSubmit} className="space-y-5">
				<div className="grid grid-cols-2 gap-3">
					<TextField
						id="firstName"
						label="First name"
						placeholder="John"
						value={formData.firstName}
						onInput={(value) => onFieldChange("firstName", value)}
						labelClassName={styles.labelClassName}
						inputClassName={styles.inputClassName}
					/>
					<TextField
						id="lastName"
						label="Last name"
						placeholder="Doe"
						value={formData.lastName}
						onInput={(value) => onFieldChange("lastName", value)}
						labelClassName={styles.labelClassName}
						inputClassName={styles.inputClassName}
					/>
				</div>

				<div>
					<label htmlFor="phone" className={styles.labelClassName}>
						Phone <span className="text-[0.85em]">(US only)</span>
					</label>
					<div className="flex">
						<div className={styles.phonePrefixClassName}>
							<span className="text-xl mr-1.5">🇺🇸</span>
							<span className="text-xs">+1</span>
						</div>
						<input
							type="tel"
							id="phone"
							placeholder="111 222 3333"
							value={formData.phone}
							onInput={(e) =>
								onFieldChange("phone", (e.target as HTMLInputElement).value)
							}
							className={styles.phoneInputClassName}
							required
						/>
					</div>
				</div>

				<TextField
					id="email"
					label="Email"
					placeholder="john.doe@email.com"
					value={formData.email}
					onInput={(value) => onFieldChange("email", value)}
					type="email"
					labelClassName={styles.labelClassName}
					inputClassName={styles.inputClassName}
				/>

				{messageParamEnabled && (
					<div className="flex items-center gap-2.5 py-2">
						<Toggle
							id="preferMessage"
							checked={preferMessage}
							onChange={(checked) => {
								setPreferMessage(checked);
								if (!checked) onFieldChange("message", "");
							}}
							accentColor={styles.successAccentColor}
							ariaLabel="I prefer to send a message"
						/>
						<label
							htmlFor="preferMessage"
							className={styles.consentPrimaryClassName}
						>
							I prefer to send a message
						</label>
					</div>
				)}

				{messageParamEnabled && preferMessage && (
					<div>
						<label htmlFor="message" className={styles.labelClassName}>
							Message
						</label>
						<textarea
							id="message"
							placeholder="Tell us about your project..."
							value={formData.message}
							onInput={(e) =>
								onFieldChange(
									"message",
									(e.target as HTMLTextAreaElement).value,
								)
							}
							className={`${styles.inputClassName} resize-none`}
							rows={4}
							required
						/>
					</div>
				)}

				{!preferMessage && (
					<div className="flex items-start gap-2.5">
						<input
							type="checkbox"
							id="consent"
							checked={formData.consent}
							onChange={(e) =>
								onFieldChange("consent", (e.target as HTMLInputElement).checked)
							}
							className={styles.checkboxClassName}
							required
						/>
						<label htmlFor="consent" className={styles.consentPrimaryClassName}>
							Consent to chat with Jade, our AI assistant
							<br />
							<span className={styles.consentSecondaryClassName}>
								By clicking this, you consent to automated calls, texts, and
								emails, and to be recorded. Your information and call recording
								will be shared with the firm if you choose to be connected.
							</span>
						</label>
					</div>
				)}

				<div>
					<button
						type="submit"
						disabled={isLoading}
						className={`${styles.submitButtonBaseClassName} ${
							isLoading
								? styles.submitButtonDisabledClassName
								: styles.submitButtonEnabledClassName
						} ${error ? "animate-shake" : ""} flex items-center justify-center`}
					>
						{isLoading ? (
							<div className="spinner" />
						) : preferMessage ? (
							"Send"
						) : (
							"CALL ME NOW"
						)}
					</button>
					{error && <div className={styles.errorClassName}>{error}</div>}
				</div>
			</form>
		</>
	);
}
